// client/src/components/ticketing/WaitlistPanel.jsx
//
// Staff view of one ticketed item's waitlist (Growth Phase 3): who is
// waiting and in what order, who has an open offer, who bought. Staff can
// offer a ticket by hand, move people up or down the queue, and take someone
// off. Seats that free up are offered automatically (refund, failed
// checkout, more capacity, an offer expiring), so this panel is for
// exceptions. A tab inside TicketConfig, same dark theme.

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const OPEN = ["waiting", "invited"];

function formatWhen(v) {
  if (!v) return "";
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function WaitlistPanel({ tenantId, eventId, itemId }) {
  const { t } = useTranslation();
  const base = `/api/ticketing/${tenantId}/event/${eventId}/waitlist`;
  const [rows, setRows] = useState([]);
  const [canReorder, setCanReorder] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(base, { params: { item_id: itemId } });
      if (data.success) {
        setRows(data.waitlist || []);
        setCanReorder(!!data.canReorder);
      }
    } catch (err) {
      setNotice({ kind: "error", text: t("ticketing.waitlist.loadError", "Could not load the waitlist.") });
    } finally {
      setLoaded(true);
    }
  }, [base, itemId, t]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okText) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      if (okText) setNotice({ kind: "success", text: okText });
      await load();
    } catch (err) {
      setNotice({ kind: "error", text: err?.response?.data?.message || t("ticketing.waitlist.actionError", "That did not work. Try again.") });
    } finally {
      setBusy(false);
    }
  };

  const open = rows.filter((r) => OPEN.includes(r.status));
  const closed = rows.filter((r) => !OPEN.includes(r.status));

  const move = (index, delta) => {
    const next = [...open];
    const [row] = next.splice(index, 1);
    next.splice(index + delta, 0, row);
    act(() => axios.put(`${base}/order`, { ids: next.map((r) => r.id) }), t("ticketing.waitlist.orderSaved", "Waitlist order saved."));
  };

  const offer = (r) => act(
    () => axios.post(`${base}/${r.id}/invite`),
    t("ticketing.waitlist.offerSent", "Offer emailed to {{email}}.", { email: r.email }),
  );

  const remove = (r) => {
    if (!window.confirm(t("ticketing.waitlist.confirmRemove", "Take {{name}} off the waitlist?", { name: r.name }))) return;
    act(() => axios.delete(`${base}/${r.id}`), t("ticketing.waitlist.removed", "Removed from the waitlist."));
  };

  const statusLabel = (r) => {
    if (r.status === "invited") {
      return t("ticketing.waitlist.statusInvited", "Offered until {{when}}", { when: formatWhen(r.invite_expires_at) });
    }
    return t(`ticketing.waitlist.status.${r.status}`, r.status);
  };

  if (!loaded) return <div className="ticket-empty-state">{t("ticketing.waitlist.loading", "Loading waitlist…")}</div>;

  return (
    <div>
      <div className="ticket-notice">
        {t("ticketing.waitlist.intro", "When a seat frees up, the next person in line is emailed an offer that lasts 24 hours. If they don't buy, it passes to the person after them.")}
      </div>
      {notice && <div className={`message message-${notice.kind}`}>{notice.text}</div>}

      {open.length === 0 ? (
        <div className="ticket-empty-state">{t("ticketing.waitlist.empty", "Nobody is on the waitlist.")}</div>
      ) : (
        <ol className="ticket-list" aria-label={t("ticketing.waitlist.queue", "Waitlist queue")}>
          {open.map((r, i) => (
            <li key={r.id} className="ticket-list-item">
              <div>
                <div className="ticket-list-item-title">
                  {i + 1}. {r.name}
                  {r.status === "invited" && <span className="ticket-badge accent">{t("ticketing.waitlist.offered", "Offered")}</span>}
                </div>
                <div className="ticket-list-item-meta">
                  {r.email} · {r.tier_name || t("ticketing.waitlist.anyTier", "Any ticket")} ·{" "}
                  {t("ticketing.waitlist.qty", "{{count}} wanted", { count: r.requested_qty })} · {statusLabel(r)}
                </div>
              </div>
              <div className="ticket-list-item-actions">
                {canReorder && (
                  <>
                    <button type="button" className="ticket-item-btn" disabled={busy || i === 0} onClick={() => move(i, -1)}
                      aria-label={t("ticketing.waitlist.moveUpFor", "Move {{name}} up", { name: r.name })}>
                      ↑
                    </button>
                    <button type="button" className="ticket-item-btn" disabled={busy || i === open.length - 1} onClick={() => move(i, 1)}
                      aria-label={t("ticketing.waitlist.moveDownFor", "Move {{name}} down", { name: r.name })}>
                      ↓
                    </button>
                  </>
                )}
                {r.status === "waiting" && (
                  <button type="button" className="ticket-item-btn" disabled={busy} onClick={() => offer(r)}>
                    {t("ticketing.waitlist.offer", "Offer a ticket")}
                  </button>
                )}
                <button type="button" className="ticket-item-btn danger" disabled={busy} onClick={() => remove(r)}>
                  {t("ticketing.waitlist.remove", "Remove")}
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {closed.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button type="button" className="ticket-item-btn" onClick={() => setShowClosed((v) => !v)}>
            {showClosed
              ? t("ticketing.waitlist.hideClosed", "Hide past entries")
              : t("ticketing.waitlist.showClosed", "Show past entries ({{count}})", { count: closed.length })}
          </button>
          {showClosed && (
            <ul className="ticket-list" style={{ marginTop: 10 }}>
              {closed.map((r) => (
                <li key={r.id} className="ticket-list-item">
                  <div>
                    <div className="ticket-list-item-title">{r.name}</div>
                    <div className="ticket-list-item-meta">{r.email} · {r.tier_name || t("ticketing.waitlist.anyTier", "Any ticket")} · {statusLabel(r)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
